import {
  Button,
} from "@mui/material";
import { useMemo } from "react";

import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

import { VisitTypeScheduleConfig } from "@caredna-marlin/utils/configurationTypes";
import { BlockScheduleRow, blockScheduleRowsToString, blockScheduleStringToRows, validateBlockSchedule } from "@caredna-marlin/utils/blockScheduling";

import BlockScheduleOption from "./BlockScheduleOption";

type Props = {
  arrivalWindowBlockSchedule: VisitTypeScheduleConfig["arrivalWindowBlockSchedule"],
  onChange: (value: VisitTypeScheduleConfig["arrivalWindowBlockSchedule"]) => void,
  setValidationErrors: (hasErrors: boolean) => void,
}

const allComplete = (rows: BlockScheduleRow[]):boolean => {
  return rows.every(row => row.start !== "" && row.end !== "")
}

const BlockSchedule = ({ arrivalWindowBlockSchedule, onChange, setValidationErrors }: Props) => {
  const blockScheduleRows = useMemo<BlockScheduleRow[]>(() => {
    return blockScheduleStringToRows(arrivalWindowBlockSchedule) || [{ start: "", end: "" }];
  }, [arrivalWindowBlockSchedule]);

  const blockScheduleValidation = useMemo(() => validateBlockSchedule(arrivalWindowBlockSchedule), [arrivalWindowBlockSchedule]);

  // Helper to update block schedule rows and trigger validation if all rows are complete.
  const updateBlockSchedule = (rows: Array<{ start: string; end: string }>) => {
    if (allComplete(rows)) {
      const string = blockScheduleRowsToString(rows);
      const result = validateBlockSchedule(string);
      onChange(result.sortedRanges || string);
      setValidationErrors(result.errors.length > 0);
    } else {
      // If not all rows are complete, update local with the complete rows only.
      const scheduleStr = blockScheduleRowsToString(rows)
      onChange(scheduleStr);
      setValidationErrors(true);
    }
  };

  return <>
    {blockScheduleRows.map((row, index) => <BlockScheduleOption
      key={index}
      row={row}
      onChange={row => {
        const newRows = [...blockScheduleRows];
        newRows[index] = row;
        updateBlockSchedule(newRows);
      }}
      onRemove={() => {
        const newRows = [...blockScheduleRows];
        newRows.splice(index, 1);
        updateBlockSchedule(newRows);
      }}
    />)}
    <Button onClick={() => updateBlockSchedule([...blockScheduleRows, { start: "", end: "" }])}>
      Add Row
    </Button>
    {/* Display any validation errors */}
    {allComplete(blockScheduleRows) && blockScheduleValidation.errors.map((error, idx) => (
      <div key={idx}>
        <WarningAmberRoundedIcon color="error" sx={{ verticalAlign: "bottom" }} />
        {error}
      </div>
    ))}
    {blockScheduleValidation.warnings.map((warning, idx) => (
        <div key={idx}>
          <WarningAmberRoundedIcon color="warning" sx={{ verticalAlign: "bottom" }} />
          {warning}
        </div>
    ))}
  </>
}

export default BlockSchedule;
